import React, { useEffect, useState } from 'react'
import Tabs from 'components/TabsRoutes'
import SupplierInfo from './SupplierInfo'
import Profile from './Profile'
import HistoricPrices from './HistoricPrices'
import RequiredFields from './RequiredFields'
import Tasks from './Tasks'
import Overview from './Overview'
import MessagesTab from './MessagesTab'
import Availability from './Availability'
import SupplierPublicProfile from 'rest/views/coordinator/supplier/Profile'
import SupplierDataTab from 'rest/views/coordinator/supplier/data/Tab'
import { RatesList } from 'containers/SupplierProfile/components/Rates'
import SupplierDemos from 'containers/SupplierProfile/components/Demos'
import useCurrentUser from 'hooks/useCurrentUser'
import { Card } from '@arnsbogroup/react-voicearchive'
import SyntheticVoice from '../SupplierProfile/components/SyntheticVoice'

const SupplierPage = ( {supplierId, history, partner} ) => {
    const initialState = {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        mobile: null,
    }

    const [state, setState] = useState(initialState)
    const currentUser = useCurrentUser()

    useEffect(() => {
        if (parseInt(supplierId)) {
            fetchSupplier(supplierId)
        }
    }, [supplierId])

    const fetchSupplier = (supplierId) => {
        fetch(`/rest/v2/supplier/${supplierId}?includeRelations=1`)
        .then((response) => response.json())
        .then((data) => {
            setState(data)
        })
        .catch((err) => {
            toastr.error(err)
        })
    }

    const onChangeHandler = (newState) => {
        const diff = Object.keys(newState).reduce((diff, key) => {
            if (state[key] === newState[key]) return diff
            return { ...diff, [key]: newState[key] }
        }, {})

        if (Object.keys(diff).length === 0 && diff.constructor === Object) {
            return
        }

        let prevState = state;
        setState(newState)

        fetch(`/rest/v2/supplier/${supplierId}`, {
            credentials: 'same-origin',
            method: 'PUT',
            body: JSON.stringify(diff),
        })
        .then((response) => response.json())
        .then((response) => {
            toastr.success('Supplier updated')
        })
        .catch((error) => {
            toastr.error(error)
            setState(prevState)
        })
    }

    const tabList = () => {

        const overview = {
            message: 'Overview',
            to: `/suppliers/${state.uid}/overview`,
            route: {
            path: '/suppliers/:supplierId/overview',
            render: (props) => (
                <Overview onChangeHandler={onChangeHandler} supplier={state} currentUser={currentUser} />
            ),
            }
        };

        const data = {
            message: 'Data (Docs, Log & Experience)',
            to: `/suppliers/${state.uid}/data`,
            route: {
            path: '/suppliers/:supplierId/data',
            exact: true,
            component: SupplierDataTab,
            }
        };

        const rates = {
            message: 'Rates',
            to: `/suppliers/${state.uid}/rates`,
            route: {
            path: '/suppliers/:supplierId/rates',
            exact: true,
            component: RatesList,
            }
        };

        const profile = {
            message: 'Profile',
            to: `/suppliers/${state.uid}/profile`,
            route: {
            path: '/suppliers/:supplierId/profile',
            render: (props) => (
                <Profile onChangeHandler={onChangeHandler} supplier={state} currentUser={currentUser} />
            ),
            }
        };

        const availability = {
            message: 'Availability',
            to: `/suppliers/${state.uid}/availability`,
            route: {
            path: '/suppliers/:supplierId/availability',
            render: (props) => (
                <Availability onChangeHandler={onChangeHandler} supplier={state} currentUser={currentUser} {...props} />
            ),
            }
        };

        const demos = {
            message: 'Demos',
            to: `/suppliers/${state.uid}/demos`,
            route: {
            path: '/suppliers/:supplierId/demos',
            render: (props) => (
                <SupplierDemos admin {...props} />
            ),
            }
        };

        const syntheticVoice = {
            message: 'Synthetic Voice',
            to: `/suppliers/${state.uid}/synthetic-voice`,
            route: {
                path: '/suppliers/:supplierId/synthetic-voice',
                render: (props) => (
                    <SyntheticVoice admin {...props} />
                ),
            }
        };
        
        const message = {
            message: 'Message',
            to: `/suppliers/${state.uid}/message`,
            route: {
                path: '/suppliers/:supplierId/message',
                render: (props) => (
                    <MessagesTab supplier={state} {...props} />
                ),
            }
        };

        const websiteProfile = {
            message: 'Website Profile',
            to: `/suppliers/${state.uid}/website-profile`,
            route: {
            path: '/suppliers/:supplierId/website-profile',
            component: SupplierPublicProfile,
            }
        };

        const openTasks = {
            message: 'Open Tasks',
            to: `/suppliers/${state.uid}/tasks/open`,
            route: {
                path: '/suppliers/:supplierId/tasks/open',
                exact: true,
                render: (props) => (
                    <Tasks history={history} onChangeHandler={onChangeHandler} supplier={state} taskState="openTasks" {...props} />
                ),
            }
        };

        const closedTasks = {
            message: 'Closed Tasks',
            to: `/suppliers/${state.uid}/tasks/closed`,
            route: {
                path: '/suppliers/:supplierId/tasks/closed',
                exact: true,
                render: (props) => (
                    <Tasks history={history} onChangeHandler={onChangeHandler} supplier={state} taskState="closedTasks" {...props} />
                ),
            }
        };

        const prices = {
            message: 'Prices (Deprecated)',
            to: `/suppliers/${state.uid}/prices`,
            route: {
                path: '/suppliers/:supplierId/prices',
                exact: true,
                render: () => (
                    <HistoricPrices supplier={state} />
                ),
            }
        };

        if (partner === 'voicetome') {
            return [
                overview,
                data,
                rates,
                profile,
                availability,
                demos,
                message,
                websiteProfile,
                openTasks,
                closedTasks,
                prices
            ];
        }

        return [
            overview,
            data,
            rates,
            profile,
            availability,
            demos,
            syntheticVoice,
            message,
            websiteProfile,
            openTasks,
            closedTasks,
            prices
        ];
    }

    return (
        <div>
            {!!state.uid &&
              <div className="bg-gray">
                    <RequiredFields reFetchSupplier={fetchSupplier} supplier={state} />
                    <div className="bg-gray-50 p-4">
                      <Card className="bg-white">
                        <SupplierInfo onChangeHandler={onChangeHandler} supplier={state} currentUser={currentUser}/>
                      </Card>
                    </div>
                    <div>
                        <Tabs
                          className="bg-gray-50"
                          childrenClassName="bg-white"
                          tabsClassName="px-4"
                          tabs={tabList()}/>
                    </div>
                </div>
            }
        </div>
    )
}

export default SupplierPage
